import { io, Socket } from "socket.io-client";
import { useEffect, useCallback, useRef, useState } from "react";
import { useAuth } from "../../taskpane/contexts/AuthContext";
import config from "../../config";
import authService from "../../taskpane/services/authService";

/**
 * Enhanced Streaming Endpoint with Room Support
 * 
 * This module provides comprehensive streaming functionality with support for:
 * - Traditional user-based messaging (backward compatible)
 * - New room-based messaging system
 * - Automatic session room creation and management
 * - Message acknowledgment and duplicate detection
 * - Connection metrics and monitoring
 * 
 * Key Features:
 * 1. Room Management: Create, join, and leave custom rooms
 * 2. Session Rooms: Automatically managed rooms for AI chat sessions
 * 3. Dual Routing: Messages can be sent to users OR rooms
 * 4. Backward Compatibility: Existing user-based code continues to work
 * 
 * Usage Examples:
 * 
 * // Basic room streaming (auto-creates AI chat room)
 * const { roomId, sendRoomMessage, isRoomReady } = useRoomStreaming('ai-chat', onMessage);
 * 
 * // Manual room management
 * const { joinRoom, leaveRoom, createAndJoinSessionRoom } = useSocketConnectionWithACK();
 * const roomId = await createAndJoinSessionRoom('ai-chat');
 * joinRoom(roomId, 'user-session');
 * 
 * // Send messages to rooms
 * await streamingApiClient.sendMessageToRoom(roomId, { type: 'ai_response', payload: data });
 */

// Singleton socket manager to prevent multiple connections
class SocketManager {
  private static instance: SocketManager;
  private socket: Socket | null = null;
  private user: any = null;
  private token: string | null = null; // Store token for reconnections
  private connectionListeners: Set<Function> = new Set();
  private messageListeners: Set<Function> = new Set();
  private receivedMessages: Set<string> = new Set(); // Track received message IDs for idempotency
  private joinedRooms: Set<string> = new Set(); // Track which rooms we've successfully joined
  private pendingRoomJoins: Map<string, { resolve: Function, reject: Function, roomType: string }> = new Map(); // Track pending room join requests
  private connectionMetrics = {
    userConnectionCount: 0,
    totalConnections: 0,
    connectedUsersCount: 0,
    isConnected: false,
    connectionId: null as string | null,
    lastConnected: undefined as Date | undefined,
    connectionDuration: undefined as number | undefined,
    reconnectCount: 0,
  };

  private constructor() {}

  static getInstance(): SocketManager {
    if (!SocketManager.instance) {
      SocketManager.instance = new SocketManager();
    }
    return SocketManager.instance;
  }

  getSocket(): Socket | null {
    return this.socket;
  }

  getConnectionMetrics() {
    return { ...this.connectionMetrics };
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  addConnectionListener(listener: Function) {
    this.connectionListeners.add(listener);
  }

  removeConnectionListener(listener: Function) {
    this.connectionListeners.delete(listener);
  }

  addMessageListener(listener: Function) {
    this.messageListeners.add(listener);
  }

  removeMessageListener(listener: Function) {
    this.messageListeners.delete(listener);
  }

  private notifyConnectionListeners(isConnected: boolean, metrics?: any) {
    this.connectionListeners.forEach(listener => {
      try {
        listener(isConnected, metrics);
      } catch (error) {
        console.error('Error in connection listener:', error);
      }
    });
  }

  private notifyMessageListeners(message: any) {
    this.messageListeners.forEach(listener => {
      try {
        listener(message);
      } catch (error) {
        console.error('Error in message listener:', error);
      }
    });
  }

  // Universal message handler with ACK and idempotency
  private handleMessage(eventType: string, data: StreamingMessage) {
    
    // Check if message has ID for duplicate detection
    if (data.messageId) {
      if (this.receivedMessages.has(data.messageId)) {
        return; // Ignore duplicate - don't process further
      }
      
      // Add to received messages set FIRST
      this.receivedMessages.add(data.messageId);
      
      // Send ACK if required (only once)
      if (data.requiresAck && this.socket) {
        this.socket.emit('ack', { messageId: data.messageId });
      }
    }
    
    // Process the message (only if not duplicate)
    const processedMessage = {
      type: eventType,
      ...data
    };
    
    // Process the message
    
    this.notifyMessageListeners(processedMessage);
  }

  // Request missed messages on reconnection
  private requestMissedMessages() {
    if (this.socket && this.user) {
      console.log('Requesting missed messages for user:', this.user.id);
      this.socket.emit('requestMissedMessages', { userId: this.user.id });
    }
  }

  // Clear received messages cache (useful for testing or memory management)
  clearReceivedMessages() {
    this.receivedMessages.clear();
    console.log('Cleared received messages cache');
  }

  // Room management methods - now returns a Promise
  joinRoom(roomId: string, roomType: string = 'custom'): Promise<void> {
    return new Promise((resolve, reject) => {
      // Store the promise resolvers for when we get the roomJoined event
      this.pendingRoomJoins.set(roomId, { resolve, reject, roomType });
      
      // Set a timeout in case the server doesn't respond
      setTimeout(() => {
        if (this.pendingRoomJoins.has(roomId)) {
          this.pendingRoomJoins.delete(roomId);
          reject(new Error(`Room join timeout for ${roomId}`));
        }
      }, 10000); // 10 second timeout
      
      if (this.socket?.connected) {
        this.socket.emit('joinRoom', { roomId, roomType });
      }
      // If not connected, the room join will be processed when socket connects
    });
  }

  leaveRoom(roomId: string) {
    if (this.socket?.connected) {
      this.socket.emit('leaveRoom', { roomId });
    }
  }

  // Get current rooms user is in
  getCurrentRooms(): string[] {
    return Array.from(this.joinedRooms);
  }

  // Check if user has joined a specific room
  isRoomJoined(roomId: string): boolean {
    return this.joinedRooms.has(roomId);
  }

  // Get all joined rooms as a set for efficient checking
  getJoinedRooms(): Set<string> {
    return new Set(this.joinedRooms);
  }

  async connect(user: any, token?: string): Promise<void> {
    console.log('🔌 SocketManager.connect called with:', {
      userId: user?.id,
      hasToken: !!token,
      tokenLength: token?.length,
      tokenPreview: token ? `${token.substring(0, 50)}...` : 'undefined'
    });
    
    // Always get a fresh token from authService to ensure it's valid
    const freshToken = token || authService.getAccessToken();
    
    // If already connected for the same user, don't create a new connection
    if (this.socket?.connected && this.user?.id === user.id) {
      console.log("SocketManager: Socket already connected for user:", user.id);
      return;
    }

    // If socket exists but not connected, need to recreate with fresh token
    if (this.socket && !this.socket.connected && this.user?.id === user.id) {
      console.log("SocketManager: Socket exists but not connected, recreating with fresh token");
      // Disconnect old socket and create new one with fresh token
      this.socket.offAny();
      this.socket.disconnect();
      this.socket = null;
      // Will continue to create new socket below with fresh token
    }

    // Disconnect existing socket if connecting for different user
    if (this.socket && this.user?.id !== user.id) {
      console.log("SocketManager: Switching user, disconnecting previous socket");
      this.disconnect();
    }

    this.user = user;
    this.token = freshToken; // Store fresh token for reference
    const backendUrl = getBackendUrl();
    const socketUrl = `${backendUrl}/realtime`;
    
    const authPayload = { 
      userId: user.id,
      token: freshToken // Send JWT token for authentication
    };
    
    console.log('🔐 Creating socket with auth payload:', {
      socketUrl: socketUrl,
      backendUrl: backendUrl,
      userId: authPayload.userId,
      hasToken: !!authPayload.token,
      tokenType: typeof authPayload.token,
      tokenValue: authPayload.token ? `${authPayload.token.substring(0, 50)}...` : 'undefined'
    });
    
    this.socket = io(socketUrl, {
      auth: authPayload,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      transports: ['websocket', 'polling'],
      forceNew: false,
      withCredentials: true, // Also send cookies if available
    });

    // Connection event handlers
    this.socket.on("connect", () => {
      console.log("SocketManager: Socket connected:", this.socket?.id);
      
      this.connectionMetrics = {
        ...this.connectionMetrics,
        isConnected: true,
        connectionId: this.socket?.id || null,
        lastConnected: new Date(),
      };

      this.notifyConnectionListeners(true, this.connectionMetrics);
      
      // Join user-specific room
      this.socket?.emit("join", { userId: user.id });
      
      // Process any pending room join requests
      if (this.pendingRoomJoins.size > 0) {
        this.pendingRoomJoins.forEach((value, roomId) => {
          this.socket?.emit('joinRoom', { roomId, roomType: value.roomType });
        });
      }
      
      // Request missed messages on reconnection
      this.requestMissedMessages();
    });

    this.socket.on("disconnect", (reason) => {
      console.log("SocketManager: Socket disconnected:", reason);
      
      this.connectionMetrics = {
        ...this.connectionMetrics,
        isConnected: false,
        connectionId: null,
        connectionDuration: undefined,
      };

      this.notifyConnectionListeners(false, this.connectionMetrics);
    });

    this.socket.on("connect_error", (error: any) => {
      console.error("❌ SocketManager: Socket connection error:", error);
      console.error("   Socket URL:", socketUrl);
      console.error("   Error details:", {
        message: error.message,
        type: error.type || 'unknown',
        description: error.description || 'none',
        context: error.context || 'none'
      });
      console.error("   Auth payload that was sent:", {
        userId: authPayload.userId,
        hasToken: !!authPayload.token,
        tokenPreview: authPayload.token ? `${authPayload.token.substring(0, 30)}...` : 'undefined'
      });
      this.connectionMetrics.reconnectCount++;
    });

    this.socket.on("error", (error) => {
      console.error("SocketManager: Socket error:", error);
    });

    // Listen for room-related events
    this.socket.on("roomJoined", (data: { roomId: string, roomType: string }) => {
      this.joinedRooms.add(data.roomId);
      
      // Resolve any pending join promise
      const pending = this.pendingRoomJoins.get(data.roomId);
      if (pending) {
        pending.resolve();
        this.pendingRoomJoins.delete(data.roomId);
      }
    });

    this.socket.on("roomLeft", (data: { roomId: string }) => {
      this.joinedRooms.delete(data.roomId);
    });

    this.socket.on("roomError", (data: { error: string, roomId?: string }) => {
      console.error('❌ Room error:', data.error, data.roomId || '');
      
      // Reject any pending join promise
      if (data.roomId) {
        this.joinedRooms.delete(data.roomId);
        const pending = this.pendingRoomJoins.get(data.roomId);
        if (pending) {
          pending.reject(new Error(data.error));
          this.pendingRoomJoins.delete(data.roomId);
        }
      }
    });

    // Listen for all message events using the ACK handler
    this.socket.onAny((eventName: string, data: any) => {
      if (eventName !== "connect" && eventName !== "disconnect" && eventName !== "connect_error" && eventName !== "connection_metrics_update" && eventName !== "roomJoined" && eventName !== "roomLeft" && eventName !== "roomError") {
        this.handleMessage(eventName, data);
      }
    });

    // Listen for connection metrics updates from server
    this.socket.on("connection_metrics_update", (metrics: any) => {
      console.log("SocketManager: Received connection metrics update:", metrics);
      this.connectionMetrics = {
        ...this.connectionMetrics,
        userConnectionCount: metrics.userConnectionCount || this.connectionMetrics.userConnectionCount,
        totalConnections: metrics.totalConnections || this.connectionMetrics.totalConnections,
        connectedUsersCount: metrics.connectedUsersCount || this.connectionMetrics.connectedUsersCount,
      };
      this.notifyConnectionListeners(this.connectionMetrics.isConnected, this.connectionMetrics);
    });
  }

  disconnect(clearAuth: boolean = false): void {
    if (this.socket) {
      console.log("SocketManager: Disconnecting socket");
      this.socket.offAny();
      this.socket.disconnect();
      this.socket = null;
    }
    
    // Clear joined rooms on disconnect
    this.joinedRooms.clear();
    
    // Reject any pending room join promises
    this.pendingRoomJoins.forEach((value) => {
      value.reject(new Error('Socket disconnected'));
    });
    this.pendingRoomJoins.clear();
    
    // Clear authentication data if requested (e.g., on logout)
    if (clearAuth) {
      this.user = null;
      this.token = null;
      console.log("SocketManager: Cleared authentication data");
    }
    
    this.connectionMetrics = {
      ...this.connectionMetrics,
      isConnected: false,
      connectionId: null,
      connectionDuration: undefined,
    };

    this.notifyConnectionListeners(false, this.connectionMetrics);
  }

  reconnect(): void {
    if (this.user) {
      this.disconnect();
      setTimeout(() => {
        // Get fresh token from authService on reconnect
        const freshToken = authService.getAccessToken();
        console.log('🔄 Reconnecting with fresh token from authService');
        this.connect(this.user, freshToken || undefined);
      }, 1000);
    }
  }

  // Force reset the socket connection (useful when stuck in connecting state)
  forceReset(): void {
    console.log('🔄 Force resetting socket connection');
    
    // Clear all state
    this.receivedMessages.clear();
    this.joinedRooms.clear();
    
    // Reject any pending room join promises
    this.pendingRoomJoins.forEach((value) => {
      value.reject(new Error('Socket force reset'));
    });
    this.pendingRoomJoins.clear();
    
    // Disconnect and reconnect if we have a user
    if (this.socket) {
      this.socket.offAny();
      this.socket.disconnect();
      this.socket = null;
    }
    
    if (this.user) {
      // Reconnect after a short delay with fresh token from authService
      setTimeout(() => {
        const freshToken = authService.getAccessToken();
        console.log('🔄 Force reset: Reconnecting with fresh token from authService');
        this.connect(this.user, freshToken || undefined);
      }, 500);
    }
  }
}

// Export singleton instance
export const socketManager = SocketManager.getInstance();

// Generic streaming message interface
export interface StreamingMessage {
  type: string;
  userId?: string; // Made optional since backend extracts from auth header
  roomId?: string; // Support for room-based messaging
  incidentId?: string;
  incident_id?: string; // Support both naming conventions
  payload?: object; // Changed from message to payload object
  data?: any;
  contact_ids?: string[];
  contact_types?: string[];
  messageId?: string; // Unique message ID for ACK system
  requiresAck?: boolean; // Whether this message requires acknowledgment
  timestamp?: string; // Message timestamp
  [key: string]: any; // Allow any additional properties
}

// Room management interfaces
export interface RoomInfo {
  roomId: string;
  roomType: string;
  memberCount: number;
  createdAt: string;
  metadata?: any;
}

export interface JoinRoomData {
  roomId: string;
  roomType: string;
}

export interface CreateSessionRoomData {
  sessionType: string;
  metadata?: any;
}

// Get backend URL configuration
const getBackendUrl = () => {
  // Use configuration from config file
  return config.BACKEND_URL;
};

// Enhanced hook using singleton socket manager
export function useStreaming() {
  const { user, getAccessToken } = useAuth();
const [connectionMetrics, setConnectionMetrics] = useState(socketManager.getConnectionMetrics());

// Authentication check with token
const hasValidAuth = useCallback(() => {
  return !!user && !!getAccessToken();
}, [user, getAccessToken]);

// Update connection metrics - only use available endpoints
const updateConnectionMetrics = useCallback(async () => {
  if (!user) return;

  try {
    // Only call the status endpoint that exists
    const statusResponse = await streamingApiClient.getUserConnectionStatus(user.id);

    setConnectionMetrics(prev => ({
      ...prev,
      isConnected: statusResponse.connected || false,
      userConnectionCount: statusResponse.userConnectionCount || prev.userConnectionCount,
      totalConnections: statusResponse.totalConnections || prev.totalConnections,
      connectedUsersCount: statusResponse.connectedUsersCount || prev.connectedUsersCount,
    }));
  } catch (error) {
    console.error("Failed to update connection metrics:", error);
  }
}, [user]);

// Connection listener
useEffect(() => {
  const connectionListener = (isConnected: boolean, metrics?: any) => {
    setConnectionMetrics(prev => ({
      ...prev,
      isConnected,
      connectionId: metrics?.connectionId || prev.connectionId,
      ...metrics
    }));
  };

  socketManager.addConnectionListener(connectionListener);

  return () => {
    socketManager.removeConnectionListener(connectionListener);
  };
}, []);

  // Initialize connection using singleton (only once)
  useEffect(() => {
    if (user && !socketManager.isConnected() && hasValidAuth()) {
      const token = getAccessToken();
      socketManager.connect(user, token);
    }
  }, [user, hasValidAuth, getAccessToken]);

  // Get socket from singleton
  const socket = socketManager.getSocket();

  // Cleanup function (no longer disconnects singleton socket)
  const cleanup = useCallback(() => {
    console.log("useStreaming: Cleaning up hook (not disconnecting singleton socket)");
  }, []);

  // Periodic metrics update
  useEffect(() => {
    if (!user || !socketManager.isConnected()) return;

    const interval = setInterval(updateConnectionMetrics, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [user, updateConnectionMetrics]);

  return { socket, cleanup, connectionMetrics, updateConnectionMetrics };
}

// Generic streaming hook that handles any message type
export function useStreamingMessages(
  incidentId: string | null,
  onMessage: (data: StreamingMessage) => void,
  messageTypes?: string[] // Optional filter for specific message types
) {
  const { socket, connectionMetrics } = useStreaming();
  const [isConnected, setIsConnected] = useState(false);
  
  // Use refs to maintain stable references and avoid unnecessary re-renders
  const onMessageRef = useRef(onMessage);
  const incidentIdRef = useRef(incidentId);
  const messageTypesRef = useRef(messageTypes);
  
  // Update refs when props change
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  
  useEffect(() => {
    incidentIdRef.current = incidentId;
  }, [incidentId]);
  
  useEffect(() => {
    messageTypesRef.current = messageTypes;
  }, [messageTypes]);

  const handleMessage = useCallback((data: StreamingMessage) => {
    console.log("DEBUG: useStreamingMessages", data);
    
    // Enhanced filtering with room support
    // Filter by incident if provided
    if (incidentIdRef.current && data.incidentId && data.incidentId !== incidentIdRef.current) {
      return;
    }

    // Filter by message types if provided
    if (messageTypesRef.current && !messageTypesRef.current.includes(data.type)) {
      return;
    }

    // Log message context (room vs user)
    if (data.roomId) {
      console.log("Room-based streaming message received:", data.roomId, data.type);
    } else {
      console.log("User-based streaming message received:", data.userId, data.type);
    }
    
    onMessageRef.current(data);
  }, []); // Empty dependency array since we use refs

  // Track socket connection state
  useEffect(() => {
    if (!socket) {
      setIsConnected(false);
      return;
    }

    // Set initial connection state
    setIsConnected(socket.connected);

    // Listen for connection state changes
    const handleConnect = () => {
      console.log("Socket connected in useStreamingMessages");
      setIsConnected(true);
    };

    const handleDisconnect = () => {
      console.log("Socket disconnected in useStreamingMessages");
      setIsConnected(false);
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) return;

    console.log("Setting up generic streaming listeners for incident:", incidentId);

    // Listen for generic 'message' event that handles all types
    socket.on("message", handleMessage);

    // Also listen for any specific event types that might be emitted directly
    socket.onAny((eventName: string, data: any) => {
      if (eventName !== "message" && eventName !== "connect" && eventName !== "disconnect") {
        // Treat any other event as a streaming message
        const streamingMessage: StreamingMessage = {
          type: eventName,
          userId: data.userId || "",
          incidentId: data.incidentId,
          payload: data.payload,
          data: data,
          ...data
        };
        handleMessage(streamingMessage);
      }
    });

    // Cleanup function
    return () => {
      console.log("Cleaning up generic streaming listeners for incident:", incidentId);
      socket.off("message", handleMessage);
      socket.offAny();
    };
  }, [socket, incidentId, handleMessage]);

  return { socket, isConnected, connectionMetrics };
}

// Simplified streaming service for direct control
export class StreamingService {
  private static instance: StreamingService;
  private socket: Socket | null = null;
  private messageHandlers: Map<string, Function[]> = new Map();

  private constructor() {}

  static getInstance(): StreamingService {
    if (!StreamingService.instance) {
      StreamingService.instance = new StreamingService();
    }
    return StreamingService.instance;
  }

  initialize(_user: any, _token: string) {
    if (this.socket?.connected) {
      console.log("Socket already connected");
      return;
    }

    const backendUrl = getBackendUrl();
    console.log("StreamingService initializing with backend URL:", backendUrl);

    this.socket = io(`${backendUrl}/realtime`, {
      auth: {},
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    this.socket.on("connect", () => {
      console.log("StreamingService socket connected");
    });

    this.socket.on("disconnect", () => {
      console.log("StreamingService socket disconnected");
    });

    // Handle generic message events
    this.socket.on("message", (data: StreamingMessage) => {
      this.emit("message", data);
      // Also emit specific type
      if (data.type) {
        this.emit(data.type, data);
      }
    });

    // Handle any other events as streaming messages
    this.socket.onAny((eventName: string, data: any) => {
      console.log("StreamingService: onAny event:", eventName);
      if (eventName !== "message" && eventName !== "connect" && eventName !== "disconnect") {
        const streamingMessage: StreamingMessage = {
          type: eventName,
          userId: data.userId || "",
          incidentId: data.incidentId,
          payload: data.payload,
          data: data,
          ...data
        };
        this.emit("message", streamingMessage);
        this.emit(eventName, streamingMessage);
      }
    });
  }

  // Send initial streaming message (when starting streaming)
  sendInitialMessage(incidentId: string, message: string, additionalData?: any) {
    if (!this.socket?.connected) {
      console.warn("Socket not connected, cannot send initial message");
      return;
    }

    const initialMessage: StreamingMessage = {
      type: "streaming_start",
      userId: "", // Will be filled by backend
      incidentId,
      payload: { message },
      timestamp: new Date().toISOString(),
      ...additionalData
    };

    this.socket.emit("initial_message", initialMessage);
    console.log("Sent initial streaming message:", initialMessage);
  }

  onMessage(handler: (data: StreamingMessage) => void) {
    this.on("message", handler);
  }

  onMessageType(type: string, handler: (data: StreamingMessage) => void) {
    this.on(type, handler);
  }

  on(event: string, handler: Function) {
    if (!this.messageHandlers.has(event)) {
      this.messageHandlers.set(event, []);
    }
    this.messageHandlers.get(event)?.push(handler);
  }

  off(event: string, handler: Function) {
    const handlers = this.messageHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  private emit(event: string, data: any) {
    const handlers = this.messageHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.messageHandlers.clear();
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

// API client for backend streaming endpoints
export class StreamingApiClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    if (baseUrl) {
      this.baseUrl = baseUrl;
    } else {
      // Use API base URL from config
      const apiBaseUrl = config.API_BASE_URL;
      
      if (apiBaseUrl) {
        // Use existing API base URL and append /streaming
        this.baseUrl = `${apiBaseUrl}/streaming`;
      } else {
        // Fallback to backend URL with /api/streaming
        const backendUrl = getBackendUrl();
        this.baseUrl = `${backendUrl}/api/streaming`;
      }
    }
  }

  /**
   * @deprecated This method is no longer needed as authentication is now handled by authService.
   * Kept for backward compatibility but does nothing.
   */
  setTokenGetter(_getToken: () => string | null) {
    // No-op: Token management is now handled by authService
    console.warn('StreamingApiClient.setTokenGetter is deprecated and no longer needed');
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    console.log("Making request to:", url);

    // Use authService to ensure token is valid and automatically refresh if needed
    const response = await authService.makeAuthenticatedRequest(url, {
      credentials: 'include', // Also send cookies if available
      ...options,
    });

    if (!response.ok) {
      throw new Error(`Streaming API error: ${response.status} - ${response.statusText}`);
    }

    return response.json();
  }


  /**
   * Send any message to a specific user
   */
  async sendMessage(userId: string, message: StreamingMessage) {
    return this.makeRequest('/emit', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        eventType: message.type,
        ...message,
      }),
    });
  }

  /**
   * Start streaming with an initial message
   */
  async startStreaming(userId: string, incidentId: string, initialMessage: string, additionalData?: any) {
    const message: StreamingMessage = {
      type: "streaming_start",
      userId,
      incidentId,
      payload: { message: initialMessage },
      timestamp: new Date().toISOString(),
      ...additionalData
    };

    return this.sendMessage(userId, message);
  }

  /**
   * Send any message to all users in an organisation
   */
  async sendMessageToOrganisation(organisationId: string, message: StreamingMessage) {
    return this.makeRequest('/emit-org', {
      method: 'POST',
      body: JSON.stringify({
        organisationId,
        eventType: message.type,
        ...message,
      }),
    });
  }

  /**
   * Send message to a specific room
   */
  async sendMessageToRoom(roomId: string, message: StreamingMessage) {
    return this.makeRequest('/emit-room', {
      method: 'POST',
      body: JSON.stringify({
        roomId,
        eventType: message.type,
        ...message,
      }),
    });
  }

  /**
   * Get information about a specific room
   */
  async getRoomInfo(roomId: string): Promise<RoomInfo> {
    return this.makeRequest(`/rooms/${roomId}`);
  }

  /**
   * Helper function to safely get and log room info
   */
  async getRoomInfoSafely(roomId: string, context: string = 'unknown'): Promise<RoomInfo | null> {
    if (!roomId) {
      console.warn(`Cannot get room info: roomId is empty (context: ${context})`);
      return null;
    }
    
    try {
      const roomInfo = await this.getRoomInfo(roomId);
      console.log(`🏠 Room info for ${context}:`, {
        roomId,
        roomInfo
      });
      return roomInfo;
    } catch (error) {
      console.error(`Failed to get room info for ${context}:`, error);
      return null;
    }
  }

  /**
   * Get all rooms for the current user
   */
  async getUserRooms(): Promise<{ rooms: RoomInfo[] }> {
    return this.makeRequest('/users/rooms');
  }

  /**
   * Create a new session room
   */
  async createSessionRoom(sessionType: string, metadata?: any): Promise<{ 
    roomId: string; 
    embedded?: boolean; 
    sessionType?: string; 
    userId?: string;
    [key: string]: any; 
  }> {
    // Flatten metadata to the same level as sessionType
    const payload = {
      sessionType,
      ...(metadata || {})
    };
    
    return this.makeRequest('/rooms/create-session', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  /**
   * Get streaming service status with multiple connection support
   */
  async getStatus() {
    return this.makeRequest('/status');
  }

  /**
   * Check if a specific user is connected (supports multiple connections)
   */
  async getUserConnectionStatus(userId: string) {
    return this.makeRequest(`/users/${userId}/status`);
  }

  /**
   * Get connection count for a specific user
   * NOTE: Not implemented in backend yet
   */
  async getUserConnectionCount(userId: string) {
    // return this.makeRequest(`/users/${userId}/connection-count`);
    console.warn('getUserConnectionCount API not implemented in backend');
    return { connectionCount: 1 }; // Fallback
  }

  /**
   * Get total connection count across all users
   * NOTE: Not implemented in backend yet
   */
  async getTotalConnectionCount() {
    // return this.makeRequest('/total-connections');
    console.warn('getTotalConnectionCount API not implemented in backend');
    return { totalConnections: 1 }; // Fallback
  }

  /**
   * Get connected users count
   * NOTE: Not implemented in backend yet
   */
  async getConnectedUsersCount() {
    // return this.makeRequest('/connected-users-count');
    console.warn('getConnectedUsersCount API not implemented in backend');
    return { connectedUsersCount: 1 }; // Fallback
  }

  /**
   * Process any message for streaming
   */
  async processMessage(message: StreamingMessage) {
    const response = await this.makeRequest('/process-message', {
      method: 'POST',
      body: JSON.stringify(message),
    });
    
    // Get room info after processing message if roomId is available
    if (message.roomId) {
      const roomInfo = await this.getRoomInfoSafely(message.roomId, 'processMessage');
      if (roomInfo) {
        // Add room info to the response
        return {
          ...response,
          roomInfo
        };
      }
    }
    
    return response;
  }

  /**
   * Clean up old pending messages
   */
  async cleanupPendingMessages() {
    return this.makeRequest('/cleanup', {
      method: 'POST',
    });
  }
}

// Advanced socket connection hook with enhanced features
export interface SocketConnectionOptions {
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
  enableMetrics?: boolean;
  metricsUpdateInterval?: number;
  onConnectionChange?: (isConnected: boolean, metrics?: any) => void;
  onError?: (error: any) => void;
}

export interface ConnectionMetrics {
  userConnectionCount: number;
  totalConnections: number;
  connectedUsersCount: number;
  isConnected: boolean;
  connectionId: string | null;
  lastConnected?: Date;
  connectionDuration?: number;
  reconnectCount: number;
}

export function useSocketConnection(options: SocketConnectionOptions = {}) {
  const { user, getAccessToken } = useAuth();
  const {
    enableMetrics = true,
    metricsUpdateInterval = 30000,
    onConnectionChange,
    onError
  } = options;

  const [connectionMetrics, setConnectionMetrics] = useState<ConnectionMetrics>(socketManager.getConnectionMetrics());
  const [isReconnecting, setIsReconnecting] = useState(false);
  const metricsIntervalRef = useRef<ReturnType<typeof setInterval>>();

  // Update connection metrics - only use available endpoints
  const updateConnectionMetrics = useCallback(async () => {
    if (!user || !enableMetrics) return;

    try {
      // Only call the status endpoint that exists
      const statusResponse = await streamingApiClient.getUserConnectionStatus(user.id);

      const newMetrics: ConnectionMetrics = {
        ...socketManager.getConnectionMetrics(),
        isConnected: statusResponse.connected || false,
        userConnectionCount: statusResponse.userConnectionCount || socketManager.getConnectionMetrics().userConnectionCount,
        totalConnections: statusResponse.totalConnections || socketManager.getConnectionMetrics().totalConnections,
        connectedUsersCount: statusResponse.connectedUsersCount || socketManager.getConnectionMetrics().connectedUsersCount,
      };

      setConnectionMetrics(newMetrics);
      onConnectionChange?.(newMetrics.isConnected, newMetrics);
    } catch (error) {
      console.error("Failed to update connection metrics:", error);
      onError?.(error);
    }
  }, [user, enableMetrics, onConnectionChange, onError]);

  // Connection listener
  useEffect(() => {
    const connectionListener = (isConnected: boolean, metrics?: any) => {
      setIsReconnecting(false);
      if (metrics) {
        setConnectionMetrics(metrics);
        onConnectionChange?.(isConnected, metrics);
      }
    };

    socketManager.addConnectionListener(connectionListener);

    return () => {
      socketManager.removeConnectionListener(connectionListener);
    };
  }, [onConnectionChange]);

  // Initialize connection using singleton (only once)
  useEffect(() => {
    if (user && !socketManager.isConnected()) {
      setIsReconnecting(true);
      const token = getAccessToken();
      socketManager.connect(user, token);
    }
  }, [user, getAccessToken]);

  // Connect function using singleton
  const connect = useCallback(() => {
    if (user) {
      setIsReconnecting(true);
      const token = getAccessToken();
      socketManager.connect(user, token);
    }
  }, [user, getAccessToken]);

  // Disconnect function using singleton
  const disconnect = useCallback((clearAuth: boolean = false) => {
    socketManager.disconnect(clearAuth);
  }, []);

  // Set up metrics interval
  useEffect(() => {
    if (!enableMetrics || !socketManager.isConnected()) return;

    metricsIntervalRef.current = setInterval(updateConnectionMetrics, metricsUpdateInterval);
    
    return () => {
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current);
      }
    };
  }, [enableMetrics, updateConnectionMetrics, metricsUpdateInterval]);

  // Manual reconnect function using singleton
  const reconnect = useCallback(() => {
    setIsReconnecting(true);
    socketManager.reconnect();
  }, []);

  // Force reset function using singleton
  const forceReset = useCallback(() => {
    setIsReconnecting(true);
    socketManager.forceReset();
  }, []);

  return {
    socket: socketManager.getSocket(),
    connectionMetrics,
    isReconnecting,
    connect,
    disconnect,
    reconnect,
    forceReset,
    updateConnectionMetrics,
  };
}

// Hook for full ACK integration with room support (recommended approach)
export function useSocketConnectionWithACK(_jwt?: string, roomId?: string) {
  const { user, getAccessToken } = useAuth();
  const receivedMessagesRef = useRef(new Set<string>());
  const [isConnected, setIsConnected] = useState(false);
  const [connectionMetrics, setConnectionMetrics] = useState(socketManager.getConnectionMetrics());
  const currentRoomRef = useRef<string | null>(null);
  const [currentRooms, setCurrentRooms] = useState<string[]>([]);

  // Message handler - ACK and duplicate detection is handled at SocketManager level
  const handleMessage = useCallback((eventType: string, data: StreamingMessage) => {
    // No need for duplicate detection here - SocketManager handles it
    // Enhanced logging for room vs user messages
    if (data.roomId) {
      console.log(`📨 Processing room message: ${data.roomId} -> ${eventType}`);
    } else if (data.userId) {
      console.log(`👤 Processing user message: ${data.userId} -> ${eventType}`);
    }
    
    // Just return the processed message for the caller to handle
    return {
      type: eventType,
      ...data
    };
  }, []);

  // Setup message listeners
  const setupMessageListener = useCallback((onProgress: (data: StreamingMessage) => void, messageTypes?: string[]) => {
    const messageListener = (message: any) => {
      // Filter by message types if provided
      if (messageTypes && !messageTypes.includes(message.type)) {
        return;
      }

      const processedMessage = handleMessage(message.type, message);
      if (processedMessage) {
        onProgress(processedMessage);
      }
    };

    socketManager.addMessageListener(messageListener);
    
    return () => {
      socketManager.removeMessageListener(messageListener);
    };
  }, [handleMessage]);

  // Connection listener
  useEffect(() => {
    const connectionListener = (connected: boolean, metrics?: any) => {
      setIsConnected(connected);
      if (metrics) {
        setConnectionMetrics(metrics);
      }
    };

    socketManager.addConnectionListener(connectionListener);
    setIsConnected(socketManager.isConnected());

    return () => {
      socketManager.removeConnectionListener(connectionListener);
    };
  }, []);

  // Enhanced room management functions
  const joinRoom = useCallback((roomId: string, roomType: string = 'custom') => {
    if (isConnected && roomId) {
      console.log(`🏠 Joining room: ${roomId} (type: ${roomType})`);
      socketManager.joinRoom(roomId, roomType);
      currentRoomRef.current = roomId;
      setCurrentRooms(prev => [...prev.filter(r => r !== roomId), roomId]);
    }
  }, [isConnected]);

  const leaveRoom = useCallback((roomId: string) => {
    if (roomId) {
      console.log(`🚪 Leaving room: ${roomId}`);
      socketManager.leaveRoom(roomId);
      if (currentRoomRef.current === roomId) {
        currentRoomRef.current = null;
      }
      setCurrentRooms(prev => prev.filter(r => r !== roomId));
    }
  }, []);

  // Create and join a session room
  const createAndJoinSessionRoom = useCallback(async (sessionType: string, metadata?: any) => {
    try {
      const response = await streamingApiClient.createSessionRoom(sessionType, metadata);
      const { roomId } = response;
      joinRoom(roomId, 'user-session');
      return roomId;
    } catch (error) {
      console.error('Failed to create session room:', error);
      throw error;
    }
  }, [joinRoom]);

  // Initialize connection
  useEffect(() => {
    if (user && !socketManager.isConnected()) {
      const token = getAccessToken();
      socketManager.connect(user, token);
    }
  }, [user, getAccessToken]);

  // Handle room joining/leaving when roomId changes
  useEffect(() => {
    if (!isConnected) return;

    // Leave previous room if we were in one
    if (currentRoomRef.current && currentRoomRef.current !== roomId) {
      leaveRoom(currentRoomRef.current);
    }

    // Join new room if provided
    if (roomId && roomId !== currentRoomRef.current) {
      joinRoom(roomId);
    }
  }, [isConnected, roomId, joinRoom, leaveRoom]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Leave current room only on component unmount
      if (currentRoomRef.current) {
        leaveRoom(currentRoomRef.current);
      }
    };
  }, []); // Empty dependency array ensures this only runs on mount/unmount

  // Clear received messages cache when user or room changes
  useEffect(() => {
    receivedMessagesRef.current.clear();
  }, [user?.id, roomId]);

  // Wrap functions in useCallback to prevent unnecessary re-renders
  const clearReceivedMessages = useCallback(() => {
    receivedMessagesRef.current.clear();
    socketManager.clearReceivedMessages();
  }, []);

  const requestMissedMessages = useCallback(() => {
    const socket = socketManager.getSocket();
    if (socket && user) {
      socket.emit('requestMissedMessages', { userId: user.id });
    }
  }, [user]);

  const forceReset = useCallback(() => {
    console.log('🔄 Force resetting socket from useSocketConnectionWithACK');
    socketManager.forceReset();
  }, []);

  return {
    socket: socketManager.getSocket(),
    isConnected,
    connectionMetrics,
    setupMessageListener,
    joinRoom,
    leaveRoom,
    createAndJoinSessionRoom,
    currentRoom: currentRoomRef.current,
    currentRooms,
    clearReceivedMessages,
    requestMissedMessages,
    forceReset
  };
}

// Hook for room-based streaming with automatic session management
export function useRoomStreaming(
  sessionType?: string,
  onMessage?: (data: StreamingMessage) => void,
  messageTypes?: string[],
  autoCreateRoom: boolean = true
) {
  const { user, getAccessToken } = useAuth();
  const [roomId, setRoomId] = useState<string | null>(null);
  const [isRoomReady, setIsRoomReady] = useState(false);
  const { 
    socket, 
    isConnected, 
    setupMessageListener, 
    joinRoom, 
    leaveRoom, 
    createAndJoinSessionRoom 
  } = useSocketConnectionWithACK();

  // Create room on mount if autoCreateRoom is true
  useEffect(() => {
    if (autoCreateRoom && sessionType && isConnected && !roomId && user) {
      createAndJoinSessionRoom(sessionType)
        .then((newRoomId) => {
          setRoomId(newRoomId);
          setIsRoomReady(true);
        })
        .catch((error) => {
          console.error('Failed to create room:', error);
        });
    }
  }, [autoCreateRoom, sessionType, isConnected, roomId, user, createAndJoinSessionRoom]);

  // Setup message listener for room messages
  useEffect(() => {
    if (!onMessage || !roomId) return;

    const cleanup = setupMessageListener((message: StreamingMessage) => {
      // Only process messages for our room or general messages
      if (message.roomId === roomId || !message.roomId) {
        onMessage(message);
      }
    }, messageTypes);

    return cleanup;
  }, [onMessage, roomId, setupMessageListener, messageTypes]);

  // Manual room management functions
  const createRoom = useCallback(async (type: string = sessionType || 'ai-chat', metadata?: any) => {
    try {
      const newRoomId = await createAndJoinSessionRoom(type, metadata);
      setRoomId(newRoomId);
      setIsRoomReady(true);
      return newRoomId;
    } catch (error) {
      console.error('Failed to create room:', error);
      throw error;
    }
  }, [sessionType, createAndJoinSessionRoom]);

  const joinExistingRoom = useCallback((targetRoomId: string, roomType: string = 'custom') => {
    joinRoom(targetRoomId, roomType);
    setRoomId(targetRoomId);
    setIsRoomReady(true);
  }, [joinRoom]);

  const leaveCurrentRoom = useCallback(() => {
    if (roomId) {
      leaveRoom(roomId);
      setRoomId(null);
      setIsRoomReady(false);
    }
  }, [roomId, leaveRoom]);

  // Send message to current room
  const sendRoomMessage = useCallback(async (message: Omit<StreamingMessage, 'roomId'>) => {
    if (!roomId) {
      throw new Error('No active room to send message to');
    }

    const roomMessage: StreamingMessage = {
      type: 'room_message', // Default type if not provided
      ...message,
      roomId
    };

    return streamingApiClient.sendMessageToRoom(roomId, roomMessage);
  }, [roomId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (roomId) {
        leaveRoom(roomId);
      }
    };
  }, []); // Only run on unmount

  return {
    socket,
    roomId,
    isConnected,
    isRoomReady,
    createRoom,
    joinExistingRoom,
    leaveCurrentRoom,
    sendRoomMessage
  };
}

// Export singleton instances
export const streamingService = StreamingService.getInstance();
export const streamingApiClient = new StreamingApiClient();
